import React, { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  isPushSubscribed,
  registerServiceWorker,
} from '../lib/pushNotifications';
import { toast } from 'sonner';
import { hasFeature } from '../utils/features';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PushNotificationManager({ authToken, compact = false, user }) {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    checkPushStatus();
  }, []);

  async function checkPushStatus() {
    const supported = isPushSupported();
    setIsSupported(supported);

    if (supported) {
      setPermission(getNotificationPermission());
      
      // Register service worker on load
      try {
        await registerServiceWorker();
      } catch (err) {
        console.error('SW registration error:', err);
      }

      const subscribed = await isPushSubscribed();
      setIsSubscribed(subscribed);
    }
  }

  async function handleSubscribe() {
    if (!authToken) {
      toast.error('Please log in to enable notifications');
      return;
    }

    setIsLoading(true);
    try {
      await subscribeToPush(API_URL, authToken);
      setIsSubscribed(true);
      setPermission('granted');
      toast.success('Push notifications enabled!', {
        description: 'You will receive alerts for shifts, certificates, and messages.',
      });
    } catch (error) {
      console.error('Subscribe error:', error);
      if (error.message.includes('denied')) {
        toast.error('Notification permission denied', {
          description: 'Please enable notifications in your browser settings.',
        });
      } else {
        toast.error('Failed to enable notifications', {
          description: error.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleUnsubscribe() {
    setIsLoading(true);
    try {
      await unsubscribeFromPush(API_URL, authToken);
      setIsSubscribed(false);
      toast.success('Push notifications disabled');
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error('Failed to disable notifications');
    } finally {
      setIsLoading(false);
    }
  }

  // Check if push notifications feature is enabled for user's company
  if (!isSupported || (user && !hasFeature(user, 'push_notifications'))) {
    return null; // Don't show anything if not supported or feature not enabled
  }

  if (compact) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
        disabled={isLoading}
        title={isSubscribed ? 'Disable notifications' : 'Enable notifications'}
        data-testid="push-notification-toggle"
        className="relative"
        style={{ color: isSubscribed ? '#16a34a' : '#1e293b' }}
      >
        {isLoading ? (
          <Loader2 className="h-7 w-7 animate-spin" />
        ) : isSubscribed ? (
          <span className="relative inline-flex items-center justify-center">
            <BellRing className="h-7 w-7" style={{ color: '#16a34a' }} />
            <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full" style={{ background: '#16a34a' }}>
              <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </span>
          </span>
        ) : (
          <span className="relative inline-flex items-center justify-center">
            <Bell className="h-7 w-7 text-slate-800" />
            <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-red-500">
              <X className="h-2.5 w-2.5 text-white" strokeWidth={3} />
            </span>
          </span>
        )}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg border border-slate-700">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          {isSubscribed ? (
            <span className="relative inline-flex">
              <BellRing className="h-5 w-5 text-green-400" />
              <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-green-500">
                <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />
              </span>
            </span>
          ) : (
            <span className="relative inline-flex">
              <BellOff className="h-5 w-5 text-slate-400" />
              <span className="absolute -bottom-1 -right-1 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-red-500">
                <X className="h-2.5 w-2.5 text-white" strokeWidth={3} />
              </span>
            </span>
          )}
          <span className="font-medium text-sm">
            {isSubscribed ? 'Notifications enabled' : 'Push notifications'}
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {isSubscribed
            ? 'You will receive shift reminders and alerts'
            : 'Get notified about shifts, certificates, and messages'}
        </p>
      </div>
      <Button
        variant={isSubscribed ? 'outline' : 'default'}
        size="sm"
        onClick={isSubscribed ? handleUnsubscribe : handleSubscribe}
        disabled={isLoading}
        data-testid="push-notification-btn"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            {isSubscribed ? 'Disabling...' : 'Enabling...'}
          </>
        ) : isSubscribed ? (
          'Disable'
        ) : (
          'Enable'
        )}
      </Button>
    </div>
  );
}

export default PushNotificationManager;
